import { buildExport, type AnyEnvelope, type ExportFile } from '@wiztime/core';
import { queries, type OpLogEntry, type Store } from '@wiztime/storage';
import { downloadTextFile, MimeType } from './download';
import { emitReminderChange } from './exportReminderStore';
import { writeStorage } from './safeStorage';
import { StorageKey } from './storageKeys';

/** "20261007" of the local calendar date of an instant. */
export function compactLocalDate(epochMs: number): string {
  const date = new Date(epochMs);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

/** Name of the file of a full export: `wiztime-export-YYYYMMDD.json`. */
export function exportFileName(epochMs: number): string {
  return `wiztime-export-${compactLocalDate(epochMs)}.json`;
}

export function exportToJson(file: ExportFile): string {
  return `${JSON.stringify(file, null, 2)}\n`;
}

export interface ExportedFile {
  readonly filename: string;
  readonly recordCount: number;
}

/** Download `records` as an export package named `filename`. */
export function downloadExport(
  records: readonly AnyEnvelope[],
  filename: string,
  nowMs: number,
): ExportedFile {
  const file = buildExport(records, new Date(nowMs).toISOString());
  downloadTextFile(filename, exportToJson(file), MimeType.Json);
  return { filename, recordCount: file.records.length };
}

/** Export every record (tombstones included, so deletions survive a round trip) and remember when. */
export async function exportAllData(store: Store, nowMs: number): Promise<ExportedFile> {
  const records = await queries.allRecords(store.repo);
  const exported = downloadExport(records, exportFileName(nowMs), nowMs);
  writeStorage(StorageKey.LastExportAt, new Date(nowMs).toISOString());
  emitReminderChange();
  return exported;
}

/** Name of a diagnostic file: `wiztime-diagnostic-YYYYMMDD.json`. */
export function diagnosticFileName(epochMs: number): string {
  return `wiztime-diagnostic-${compactLocalDate(epochMs)}.json`;
}

/**
 * The diagnostic file (data dictionary §11): a full export plus the operation log under its own
 * top-level key, which an import ignores. Not counted as a backup: the export reminder is not reset.
 */
export function buildDiagnosticFile(
  records: readonly AnyEnvelope[],
  operationLog: readonly OpLogEntry[],
  nowMs: number,
): ExportFile & { readonly operationLog: readonly OpLogEntry[] } {
  return { ...buildExport(records, new Date(nowMs).toISOString()), operationLog };
}

export async function exportDiagnosticFile(store: Store, nowMs: number): Promise<ExportedFile> {
  const records = await queries.allRecords(store.repo);
  // Oldest first, as a reader of the file follows the events.
  const log = [...(await store.repo.listOpLog())].reverse();
  const file = buildDiagnosticFile(records, log, nowMs);
  const filename = diagnosticFileName(nowMs);
  downloadTextFile(filename, exportToJson(file), MimeType.Json);
  return { filename, recordCount: file.records.length };
}
